// What the tables page needs to turn a table into something printable:
// the address a guest's phone should open, and the QR code for it.
//
// The address is `<public menu address><qrPath>`. The qrPath ("/saffron-house/t/A7KQ")
// comes from the API; the public menu address is where the menu is served from, which
// is a different host from this admin, so it is configured (VITE_PUBLIC_MENU_URL) or
// typed once on the page — never guessed from the admin's own address, because a QR
// that points at the admin would be printed on every table before anyone noticed.

import QRCode from 'qrcode';

const STORAGE_KEY = 'tabletop.publicMenuUrl';

/** "https://menu.example.in/anything" → "https://menu.example.in"; anything that is not a web address → null. */
export function normalizeMenuBase(raw: string | null | undefined): string | null {
  const text = raw?.trim();
  if (!text) return null;
  try {
    const url = new URL(text);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.username || url.password) return null;
    return url.origin;
  } catch {
    return null;
  }
}

export function configuredMenuBase(): string | null {
  return normalizeMenuBase(import.meta.env.VITE_PUBLIC_MENU_URL as string | undefined);
}

export function savedMenuBase(): string | null {
  try {
    return normalizeMenuBase(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveMenuBase(base: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, base);
  } catch {
    /* private window: the address just has to be typed again next time */
  }
}

export function tableUrl(base: string, qrPath: string): string {
  return `${base}${qrPath}`;
}

const QR_OPTIONS = { errorCorrectionLevel: 'M', margin: 2 } as const;

export function qrSvg(url: string): Promise<string> {
  return QRCode.toString(url, { ...QR_OPTIONS, type: 'svg' });
}

export function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** A PNG for the print shop, drawn by the browser's canvas. */
export function qrPng(url: string): Promise<string> {
  return QRCode.toDataURL(url, { ...QR_OPTIONS, width: 1024 });
}

export function fileSafe(label: string): string {
  return label.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'table';
}

export function download(filename: string, href: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
